import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  postOpenAiCompletionSearchRequest,
  setSearchLoading,
  resetOpenAiCompletionSearchState,
} from '@client/model/openAiCompletionSearchModel';
import { selectOpenAiCompletionSearch } from '@client';

interface UseOpenAiCompletionSearchParams {
  requesterId?: string;
  aiPromptType?: string;
  formId?: string;
}

interface UseOpenAiCompletionSearchCallbacks {
  onSuccess?: (data: OpenAiCompletionDbRecord) => void;
  onError?: () => void;
}

interface UseOpenAiCompletionSearchResult {
  isSearching: boolean;
  startSearch: (
    params: UseOpenAiCompletionSearchParams,
    callbacks?: UseOpenAiCompletionSearchCallbacks,
  ) => void;
}

export function useOpenAiCompletionSearch(): UseOpenAiCompletionSearchResult {
  const dispatch = useDispatch();
  const searchState = useSelector(selectOpenAiCompletionSearch);
  const [searchCallbacks, setSearchCallbacks] = useState<UseOpenAiCompletionSearchCallbacks | null>(
    null,
  );

  const startSearch = (
    params: UseOpenAiCompletionSearchParams,
    callbacks?: UseOpenAiCompletionSearchCallbacks,
  ) => {
    if (!params.requesterId || !params.aiPromptType || !params.formId) {
      return;
    }

    dispatch(resetOpenAiCompletionSearchState());
    dispatch(setSearchLoading(true));
    setSearchCallbacks(callbacks || null);

    dispatch(postOpenAiCompletionSearchRequest(params));
  };

  // Monitor search results and call callbacks
  useEffect(() => {
    if (searchState.isLoading || !searchCallbacks) return;

    // Search found existing data
    if (searchState.data && !!searchState.data.results) {
      searchCallbacks.onSuccess?.(searchState.data);
      setSearchCallbacks(null);
      return;
    } else {
      // Search returned data but without results or Error occured
      searchCallbacks.onError?.();
      setSearchCallbacks(null);
      return;
    }
  }, [searchState.data, searchState.error, searchState.isLoading, searchCallbacks]);

  return {
    isSearching: searchState.isLoading,
    startSearch,
  };
}
